import { handleApiRequest } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountOrganizationJobPositionContractTypeChoices,
  AuthAccountOrganizationJobPositionDaysOffChoices,
  AuthAccountOrganizationJobPositionDegreesChoices,
  AuthAccountOrganizationJobPositionLanguagesChoices,
  AuthAccountOrganizationJobPositionLocationTypeChoices,
  AuthAccountOrganizationJobPositionNativeLanguagesChoices,
  AuthAccountOrganizationJobPositionPaymentTermChoices,
  CreateJobPositionMutation,
  CreateJobPositionMutationVariables,
  useCreateJobPositionMutation,
} from "@graphql/generated";
import { useOrganizationStore } from "@store/useOrganizationStore";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

export interface ICreateJobPositionValues {
  cities?: { id: string }[];
  hiringInCanada?: boolean | null;
  locationType?: { value: AuthAccountOrganizationJobPositionLocationTypeChoices };
  description?: string;
  performanceExpectation?: string;
  degrees?: { value: AuthAccountOrganizationJobPositionDegreesChoices }[];
  languages?: { value: AuthAccountOrganizationJobPositionLanguagesChoices }[];
  nativeLanguages?: { value: AuthAccountOrganizationJobPositionNativeLanguagesChoices }[];
  requiredDocuments?: (string | { value: string })[];
  otherBenefits?: string[];
  daysOff?: { value: AuthAccountOrganizationJobPositionDaysOffChoices }[];
  employerQuestions?: string[];
  contractType?: { value: AuthAccountOrganizationJobPositionContractTypeChoices };
  paymentTerm?: { value: AuthAccountOrganizationJobPositionPaymentTermChoices };
  workingStartAt?: { value: string };
  workingEndAt?: { value: string };
  title?: string;
  vaccancy?: number;
  validityDays?: number;
  startAt?: string;
  skills?: string[];
  workExperienceYearsRange?: [number, number];
  ageRange?: [number, number];
  benefits?: string[];
  salaryRange?: [number, number];
}
interface IUseCreateJobPositionParams {
  handleNextStep: () => void;
  values: Pick<
    ICreateJobPositionValues,
    "title" | "vaccancy" | "validityDays" | "startAt" | "locationType" | "hiringInCanada" | "description" | "cities"
  >;
}

export const useCreateJobPosition = () => {
  const { selectedOrganization } = useOrganizationStore();
  const [createJobPosition, { loading }] = useCreateJobPositionMutation();
  const [, setSearchParams] = useSearchParams();

  const handleSubmit = async ({ handleNextStep, values }: IUseCreateJobPositionParams) => {
    if (!selectedOrganization) {
      toast.error("Please select an organization");
      return;
    }

    await handleApiRequest<CreateJobPositionMutation, CreateJobPositionMutationVariables>({
      apiFunction: createJobPosition,
      variables: {
        organization: selectedOrganization.id,
        ...values,
        performanceExpectation: "",
        description: values.description ?? "",
        cities: values.cities?.map((city) => city.id),
        hiringInCanada: values.hiringInCanada,
        locationType: values.locationType?.value,
      },
      componentName: "AddJobPosition",
      actionName: "createJobPosition",
      onSuccess: (result) => {
        const jobPositionId = result.data?.account.organization?.createJobPosition?.organizationJobPosition?.id;
        if (!jobPositionId) return;

        setSearchParams({ id: jobPositionId });
        handleNextStep();
      },
      successMessage: "Job post created successfully",
      fallbackErrorMessage: "Failed to create job post",
      extraErrorContext: {
        organizationId: selectedOrganization.id,
      },
    });
  };

  return {
    handleSubmit,
    loading,
  };
};
